import { Config } from '../config';
import { History, MigrateRoutePayload, Route, RouteFileVersion, RouteFileVersionWithDeployment } from '../interfaces';
import { HttpService } from './http.service';

const path = `${Config.API}/api/v1/routes`;
const routeFileVersionsPath = `${Config.API}/api/v1/route-file-versions`;

export abstract class RouteService {
  public static getAll = (): Promise<Route[]> => {
    return HttpService.fetchAuthorized(path).then((res) => {
      if (!res) {
        return [];
      }

      return res;
    });
  };

  public static create = (body: Partial<Route>): Promise<any> => {
    return HttpService.fetchAuthorized(path, {
      method: 'POST',
      body: JSON.stringify(body),
    }).then((res) => res);
  };

  public static get = (id: number, silent = false, version = ''): Promise<Route> => {
    let url = `${path}/${id}`;
    if (version) {
      url += `?version=${encodeURIComponent(version)}`;
    }

    return HttpService.fetchAuthorized(url, undefined, undefined, silent).then((res) => res);
  };

  public static getHistory = (id: number): Promise<History<Route>[]> => {
    return HttpService.fetchAuthorized(`${path}/${id}/history`).then((res) => res);
  };

  public static update = (id: number, body: Partial<Route>): Promise<any> => {
    return HttpService.fetchAuthorized(`${path}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }).then((res) => res);
  };

  public static updateMap = (id: number, contents: string): Promise<any> => {
    return HttpService.fetchAuthorized(`${path}/${id}/map`, {
      method: 'PUT',
      body: JSON.stringify({ contents }),
    }).then((res) => res);
  };

  public static migrateRoute = (id: number, body: Partial<MigrateRoutePayload>): Promise<any> => {
    return HttpService.fetchAuthorized(`${path}/${id}/migrate`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }).then((res) => res);
  };

  public static delete = (id: number): Promise<any> => {
    return HttpService.fetchAuthorized(`${path}/${id}`, {
      method: 'DELETE',
    }).then((res) => res);
  };

  public static loadRawRouteDefinition = (routeId: string | number, version = ''): Promise<string> => {
    return RouteService.get(routeId as any, true, version).then((res) => {
      const url = res.s3key;

      const headers = new Headers();
      headers.append('pragma', 'no-cache');
      headers.append('cache-control', 'no-cache');

      const init = {
        method: 'GET',
        headers,
      };

      const request = new Request(url);

      return fetch(request, init).then(
        (res2) => {
          if (res2.status !== 200) {
            console.warn(res2);
            return null;
          }
          return res2.text();
        },
        (err) => {
          return '{}';
        },
      );
    });
  };

  public static getFileVersions = (): Promise<RouteFileVersion[]> => {
    return HttpService.fetchAuthorized(routeFileVersionsPath).then((res) => {
      if (!res) {
        return [];
      }
      return res;
    });
  };

  // Similar to getFileVersions but also includes deployment data
  public static getRoutefileHistory = (id: number): Promise<RouteFileVersionWithDeployment[]> => {
    return HttpService.fetchAuthorized(`${path}/${id}/file-versions`).then((res) => res);
  };

  public static getZonePDFs = (): Promise<Record<string, string>> => {
    return HttpService.fetchAuthorized(`${Config.API}/api/v1/zone-pdfs`, {
      method: 'GET',
    }).then((res) => res);
  };
}
